import classNames from "classnames";
import { useLocation } from "react-router-dom";
import { FC, memo, useState, useEffect, useMemo, useCallback } from "react";
import { useAppContext } from "../../context/app.context";
import { debounce } from "lodash";
import { ReactComponent as RailyLogo } from "../../assets/images/raily_logo.svg";
import { ReactComponent as MarketplaceLogo } from "../../assets/images/marketplace_logo.svg";
import { ReactComponent as MarketplaceLogoFull } from "../../assets/images/marketplace_logo_full.svg";
import { ReactComponent as CargoLogo } from "../../assets/images/cargo_logo.svg";
import { ReactComponent as CargoLogoFull } from "../../assets/images/cargo_logo_full.svg";
import AppAsideMinifiedNavigationComponent from "./navigation/minified/app-aside-minified-navigation.component";
import AppAsidePinButtonComponent from "./app-aside-pin-button.component";
import AppAsideExpandedNavigationComponent from "./navigation/expanded/app-aside-expanded-navigation.component";
import appAsideNavigationHelper from "./navigation/common/app-aside-navigation.helper";
import AppLogoHorizontalComponent from "../../common/components/app-logo/app-logo-horizontal.component";
import appConfig from "../../app.config";
import LinkButtonComponent from "../../common/components/button/link/link-button.component";
import useAbort from "../../common/hooks/use-abort";
import useMetaAttentionCounters from "../../common/services/meta/attention-counters/use-meta-attention-counters";
import MetaAttentionCounters from "../../common/services/meta/attention-counters/meta-attention-counters";
import companyLicenseService from "../../common/services/company-license/company-license.service";

const AppAsideComponent: FC = memo(() => {
  const { isAsidePanelPinned, pinAsidePanel, unpinAsidePanel } =
    useAppContext();

  const marketplaceUrl = appConfig.marketplaceUrl;
  const cargoUrl = appConfig.cargoUrl;

  const { pathname } = useLocation();
  const { selectedAppLanguage, user } = useAppContext();

  const [attentionCountersData, setAttentionCountersData] =
    useState<MetaAttentionCounters | null>(null);
  const [isAsidePanelExpanded, setIsAsidePanelExpanded] = useState(false);
  const [shouldExpandAsidePanel, setShouldExpandAsidePanel] = useState(false);
  const [expandedGroupsKeys, setExpandedGroupsKeys] = useState<string[]>([]);

  const optimizerCargoLicenseStatusAbort = useAbort();
  const [isOptimizerVisible, setIsOptimizerVisible] = useState<boolean>(false);

  const metaAttentionCounters = useMetaAttentionCounters();
  const metaAttentionCountersAbort = useAbort();

  useEffect(() => {
    metaAttentionCounters.load(metaAttentionCountersAbort.signal);

    return metaAttentionCountersAbort.revoke;
  }, []);

  useEffect(() => {
    if (!metaAttentionCounters.data) {
      return;
    }

    setAttentionCountersData(metaAttentionCounters.data);
  }, [metaAttentionCounters.data]);

  const navigationDefinition = useMemo(() => {
    return appAsideNavigationHelper.getNavigationDefinitionForUserRoles(
      user!.roles,
      isOptimizerVisible,
      attentionCountersData ?? undefined,
    );
  }, [
    selectedAppLanguage,
    user?.roles,
    attentionCountersData,
    isOptimizerVisible,
  ]);

  useEffect(() => {
    const expandedGroupKeysByDefault =
      appAsideNavigationHelper.getListOfDefaultOpenedNavigationItemKeys(
        navigationDefinition,
        pathname,
      );

    setExpandedGroupsKeys(expandedGroupKeysByDefault);
  }, [navigationDefinition]);

  const manageIsExpanded = useCallback(
    debounce((isExpanded: boolean) => {
      setIsAsidePanelExpanded(isExpanded);
    }, 200),
    [],
  );

  useEffect(() => {
    manageIsExpanded(shouldExpandAsidePanel);
  }, [shouldExpandAsidePanel]);

  const isExpanded = isAsidePanelPinned || isAsidePanelExpanded;

  const toggleAsidePanelPin = () => {
    if (isAsidePanelPinned) {
      unpinAsidePanel();
      return;
    }

    pinAsidePanel();
  };

  const handleMouseEnter = () => {
    setShouldExpandAsidePanel(true);
  };

  const handleMouseLeave = () => {
    setShouldExpandAsidePanel(false);
  };

  useEffect(() => {
    companyLicenseService
      .getCompanyLicenseStatus(optimizerCargoLicenseStatusAbort.signal)
      .then((value) => {
        setIsOptimizerVisible(value);
      });

    return optimizerCargoLicenseStatusAbort.revoke;
  }, []);

  const ExpandedContent = (
    <>
      <div className="app_aside_logo_wrapper">
        <AppLogoHorizontalComponent className="app_aside_logo" />
      </div>
      <AppAsideExpandedNavigationComponent
        attentionCountersData={attentionCountersData}
        expandedGroupsKeys={expandedGroupsKeys}
        setExpandedGroupsKeys={setExpandedGroupsKeys}
        isExpanded={isExpanded}
        navigationDefinition={navigationDefinition}
      />
      <LinkButtonComponent
        openInNewTab
        to={marketplaceUrl}
        classNames={{
          root: "app_aside_marketplace_link",
        }}
      >
        <MarketplaceLogoFull
          className="app_aside_marketplace_logo_full"
          data-test-id="app-aside-marketplace-link"
        />
      </LinkButtonComponent>
      <LinkButtonComponent
        openInNewTab
        to={cargoUrl}
        classNames={{
          root: "app_aside_cargo_link",
        }}
      >
        <CargoLogoFull
          className="app_aside_cargo_logo_full"
          data-test-id="app-aside-cargo-link"
        />
      </LinkButtonComponent>
      <AppAsidePinButtonComponent
        isPinned={isAsidePanelPinned}
        onClick={toggleAsidePanelPin}
        idForTesting="app-aside-pin-button"
      />
    </>
  );

  const MinifiedContent = (
    <>
      <div className="app_aside_logo_wrapper">
        <RailyLogo className="app_aside_logo" />
      </div>
      <AppAsideMinifiedNavigationComponent
        isOptimizerVisible={isOptimizerVisible}
      />

      <div className="app_aside_marketplace_logo_wrapper">
        <MarketplaceLogo className="app_aside_marketplace_logo_small" />
      </div>

      <div className="app_aside_cargo_logo_wrapper">
        <CargoLogo className="app_aside_cargo_logo_small" />
      </div>

      <AppAsidePinButtonComponent
        isPinned={isAsidePanelPinned}
        onClick={toggleAsidePanelPin}
        idForTesting="app-aside-pin-button"
      />
    </>
  );

  const Content = isExpanded ? ExpandedContent : MinifiedContent;

  return (
    <aside
      className={classNames("app_aside", isExpanded && "expanded")}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
    >
      {Content}
    </aside>
  );
});

export default AppAsideComponent;
